/**
 * L'I18N E' PREDISPOSTO E **NON E' IN USO**. E' una scelta, non un lavoro interrotto.
 *
 * ⚠️ Questo file inizializza i18next, `/translations` ricarica le sue risorse, e
 * `public/locales/it-IT/common.json` esiste. Ma `useTranslation` **non compare in nessuno dei
 * 1.198 file dell'applicativo**: ogni scritta e' italiano cablato nel JSX. Verificato il
 * 05-09-2026, ed e' il motivo per cui questa intestazione esiste — senza, chi apre il file
 * conclude ragionevolmente il contrario.
 *
 * Cosa NON fare, di conseguenza:
 *
 * - **non scrivere `t('qualche.chiave')`** in un componente: la chiave non esiste in nessun
 *   catalogo, e i18next renderebbe la chiave stessa. Si otterrebbe una scritta peggiore di
 *   quella di prima, e in una sola pagina su 223.
 * - **non dedurre da qui che l'app sia traducibile**: accenderla vuol dire estrarre le stringhe
 *   di 49 domini. E' un progetto, non una rifinitura da infilare in un'altra modifica.
 *
 * Perche' allora l'impalcatura resta: perche' costa nulla tenerla e riscriverla costerebbe una
 * giornata, e perche' `changeLanguage` e `dayjs.locale` qui sotto servono comunque — le date
 * dell'applicativo si formattano in italiano attraverso questo file.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import { STORAGE_KEYS } from '@/shared/lib/constants';
import { env } from '@/shared/lib/env';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/it';
import { leggiChiave, writeKey } from '@/shared/lib/storage';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale('it');

const stored = leggiChiave(STORAGE_KEYS.lang);
const initialLang = stored ?? env.VITE_DEFAULT_LANG;

void i18next
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: initialLang,
    fallbackLng: 'it-IT',
    /*
     * ⚠️ `currentOnly`, perche' il predefinito e' `'all'`: con quello i18next chiede **due**
     * file per ogni lingua regionale — `it-IT` **e** `it` — e da noi il secondo non esiste. Sul
     * web e' un 404 che nessuno guarda; dentro l'APK sono sei richieste fallite in undici
     * secondi, viste nei log del telefono, con la loro eccezione Java ogni volta.
     *
     * Non cambia cosa si vede: `fallbackLng` copre gia' il caso in cui il dispositivo dichiari
     * la lingua nuda. Toglie solo domande a cui sappiamo gia' che non c'e' risposta.
     */
    load: 'currentOnly',
    debug: import.meta.env.DEV,
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    backend: {
      // Local fallback bundle; real translations come from BE post-login
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    react: { useSuspense: false },
  });

export async function changeLanguage(lang: string): Promise<void> {
  writeKey(STORAGE_KEYS.lang, lang);
  await i18next.changeLanguage(lang);
}

export { i18next };
